import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import ActionsModalPopup, {
  ActionButtonConfig,
} from "../../../../../../shared/components/modals/action-popup";
import CustomStepper from "../../../../../../shared/components/stepper/steppers";
import useNotification from "../../../../../../shared/hooks/useNotification";
import { useTransactionCertificateStore } from "../../../../../../store/transaction-certificates.store";
import {
  ITcComponentCertifiedWeightInformation,
  ITcComponentRawMaterialInformation,
  ITcProductComponentInformation,
} from "../../../tc-models/tc-req-dto/tc-certified-product-details.req_dto";
import ComponentInfo from "./product-details/component-info";
import ComponentRawMaterialInfo from "./product-details/component-rm-info";
import ComponentCertifiedWeight from "./product-details/component-certified-weight";
import { formatTcV4Decimal } from "../../../tc-v4-decimals";
import { validateClaimsCategoryCombinationsForSet } from "./product-details/claims-category.utils";

interface EditComponentModalProps {
  open: boolean;
  articleNo: string;
  productCategoryCode?: string;
  productDetailCode?: string;
  tcStandards?: string[];
  // The component being added/edited (empty defaults for "add").
  component: ITcProductComponentInformation;
  isEdit: boolean;
  isSaving?: boolean;
  isReturn?: boolean;
  onClose: () => void;
  onSave: (component: ITcProductComponentInformation) => Promise<boolean>;
}

const buildEmptyComponentInfo = (
  articleNo: string,
  component: ITcProductComponentInformation
): ITcProductComponentInformation => ({
  tcAssociatedProductArticleNo: articleNo,
  tcProductComponentNo: component.tcProductComponentNo || "",
  tcProductComponentDetail: component.tcProductComponentDetail || "",
  // Preserve "" for a new component so the weight input starts empty (86ajey1xz)
  tcProductComponentNetShippingWeight: component.tcProductComponentNetShippingWeight ?? "",
  tcProductComponentInfoFiberLength: component.tcProductComponentInfoFiberLength || "",
  tcProductComponentInfoYarnCount: component.tcProductComponentInfoYarnCount || "",
  tcProductComponentInfoFabricWeightAndConstruct:
    component.tcProductComponentInfoFabricWeightAndConstruct || "",
  tcProductComponentInfoFabricWeightOfFinishedProduct:
    component.tcProductComponentInfoFabricWeightOfFinishedProduct || "",
  tcProductComponentInfoTechnicalSpecifications:
    component.tcProductComponentInfoTechnicalSpecifications || "",
  tcProductComponentInfoAdditional: component.tcProductComponentInfoAdditional || "",
  tcProductComponentCertifiedWeightInformation:
    component.tcProductComponentCertifiedWeightInformation || [],
  tcProductComponentRawMaterialInformation:
    component.tcProductComponentRawMaterialInformation || [],
  componentId: component.componentId,
});

/**
 * The nested "Edit Component" modal: 3 steppers (Component Information → Component Raw Material
 * Information → Component Certified Weight Information). Saving on the last step posts the
 * component (via the parent onSave) and, on success, closes back to the workspace.
 */
const EditComponentModal: React.FC<EditComponentModalProps> = ({
  open,
  articleNo,
  productCategoryCode = "",
  productDetailCode = "",
  tcStandards = [],
  component,
  isEdit,
  isSaving = false,
  isReturn = false,
  onClose,
  onSave,
}) => {
  const { notify } = useNotification();
  const { isRestrictPopUpSave, setRestrictPopUpSave } =
    useTransactionCertificateStore();

  const [activeStep, setActiveStep] = useState(0);
  const [componentInfo, setComponentInfo] = useState<ITcProductComponentInformation>(
    buildEmptyComponentInfo(articleNo, component)
  );
  const [rawMaterialInfo, setRawMaterialInfo] = useState<ITcComponentRawMaterialInformation[]>(
    component.tcProductComponentRawMaterialInformation || []
  );
  const [certifiedWeightInfo, setCertifiedWeightInfo] = useState<
    ITcComponentCertifiedWeightInformation[]
  >(component.tcProductComponentCertifiedWeightInformation || []);

  const componentInfoRef = useRef<any>(null);
  const rawMaterialInfoRef = useRef<any>(null);
  const certifiedWeightInfoRef = useRef<any>(null);

  // Re-seed when the modal opens or the target component changes (switch edit row).
  // NOTE: depend on the STABLE componentId, not the `component` object — the parent re-creates a
  // fresh `component` object on every render, so depending on the object reference re-fires this
  // effect each render (it also writes the Zustand store), causing an infinite update loop.
  useEffect(() => {
    if (open) {
      setActiveStep(0);
      setComponentInfo(buildEmptyComponentInfo(articleNo, component));
      setRawMaterialInfo(component.tcProductComponentRawMaterialInformation || []);
      setCertifiedWeightInfo(component.tcProductComponentCertifiedWeightInformation || []);
      setRestrictPopUpSave(false);
    }
  }, [open, component?.componentId]);

  const componentNetWeight =
    Number(componentInfo.tcProductComponentNetShippingWeight) || 0;

  const handleNext = async () => {
    try {
      // C-Step 1: Component Information (Formik form)
      if (activeStep === 0) {
        const formRef = componentInfoRef.current;
        if (formRef && typeof formRef.validateForm === "function") {
          const errors = await formRef.validateForm();
          Object.keys(errors).forEach((fieldName) => {
            formRef.setFieldTouched(fieldName, true, false);
          });
          if (Object.keys(errors).length === 0) {
            await formRef.submitForm();
            const values = formRef.values;
            setComponentInfo((prev) => ({
              ...prev,
              ...values,
              tcAssociatedProductArticleNo: articleNo,
              tcProductComponentNetShippingWeight:
                parseFloat(values.tcProductComponentNetShippingWeight?.toString() || "0") || 0,
            }));
            setActiveStep(1);
          } else {
            notify("Please fill in the required values", "error");
          }
        }
        return;
      }

      // C-Step 2: Component Raw Material Information (editable table)
      if (activeStep === 1) {
        const rmRef = rawMaterialInfoRef.current;
        const isEditing =
          rmRef?.editingRowIndex !== undefined && rmRef.editingRowIndex !== -1;
        if (isEditing) {
          notify("Please save or cancel the current row before proceeding", "error");
          return;
        }
        const rows: ITcComponentRawMaterialInformation[] = rmRef?.getRows
          ? rmRef.getRows()
          : rawMaterialInfo;
        if (!rows || rows.length === 0) {
          notify("At least one Component Raw Material entry is required", "error");
          return;
        }
        const total = rmRef?.getTotalPercentage ? rmRef.getTotalPercentage() : 0;
        if (total > 100) {
          notify(
            "Component raw material percentages cannot exceed 100%. Current total: " +
              formatTcV4Decimal(total || 0) +
              "%",
            "error"
          );
          return;
        }
        if (Math.abs(total - 100) > 0.01) {
          notify(
            "Component raw material percentages must total 100%. Current total: " +
              formatTcV4Decimal(total || 0) +
              "%",
            "error"
          );
          return;
        }
        if (rmRef && typeof rmRef.validateClaimsCategories === "function") {
          if (!rmRef.validateClaimsCategories()) return;
        }
        setActiveStep(2);
        setRestrictPopUpSave(false);
        return;
      }
    } catch (err) {
      console.error("Error in component handleNext:", err);
      notify("An unexpected error occurred. Please try again.", "error");
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
    setRestrictPopUpSave(false);
  };

  const handleSaveComponent = async () => {
    const compClaims: string[] = [];
    (rawMaterialInfo || []).forEach((r: any) => {
      if (r?.claimsCategory && String(r.claimsCategory).trim()) {
        compClaims.push(String(r.claimsCategory).trim());
      }
    });
    (certifiedWeightInfo || []).forEach((cw: any) => {
      const claim = cw?.tcProductClaimsCategory || cw?.claimsCategory || cw?.tcClaimsCategory;
      if (claim && String(claim).trim()) {
        compClaims.push(String(claim).trim());
      }
    });

    const validation = validateClaimsCategoryCombinationsForSet(
      compClaims,
      "component",
      componentInfo.tcProductComponentNo || "Component"
    );
    if (!validation.isValid && validation.errorMessage) {
      notify(validation.errorMessage, "error");
      return;
    }

    // C-Step 3 is read-only/derived; assemble the full component and post it.
    const finalComponent: ITcProductComponentInformation = {
      ...componentInfo,
      tcAssociatedProductArticleNo: articleNo,
      tcProductComponentRawMaterialInformation: rawMaterialInfo,
      tcProductComponentCertifiedWeightInformation: certifiedWeightInfo,
    };
    await onSave(finalComponent);
  };

  const steps = [
    {
      label: "Component Information",
      content: (
        <ComponentInfo
          formRef={componentInfoRef}
          componentInfoData={componentInfo}
          productCategoryCode={productCategoryCode}
          productDetailCode={productDetailCode}
          isReturn={isReturn}
          setComponentInfoData={(data) =>
            setComponentInfo((prev) => ({
              ...prev,
              ...data,
              tcAssociatedProductArticleNo: articleNo,
            }))
          }
        />
      ),
    },
    {
      label: "Component Raw Material Information",
      content: (
        <ComponentRawMaterialInfo
          formRef={rawMaterialInfoRef}
          tcStandards={tcStandards}
          rawMaterialInfoData={rawMaterialInfo}
          setRawMaterialInfoData={(data) => setRawMaterialInfo(data)}
          componentNetWeight={componentNetWeight}
        />
      ),
    },
    {
      label: "Component Certified Weight Information",
      content: (
        <ComponentCertifiedWeight
          formRef={certifiedWeightInfoRef}
          tcStandards={tcStandards}
          setCertifiedWeightInfoData={(data) => setCertifiedWeightInfo(data)}
          rawMaterialInfoData={rawMaterialInfo}
          componentNetWeight={componentNetWeight}
        />
      ),
    },
  ];

  const actions: ActionButtonConfig[] = [
    ...(activeStep !== 0
      ? [
          {
            label: "Back",
            onClick: handleBack,
            variant: "contained" as "contained",
            color: "default" as "default",
            disabled: activeStep === 1 ? isRestrictPopUpSave : false,
          },
        ]
      : []),
    {
      label: activeStep === steps.length - 1 ? "Save" : "Next",
      onClick: activeStep === steps.length - 1 ? handleSaveComponent : handleNext,
      variant: "contained" as "contained",
      color: "primary" as "primary",
      disabled: activeStep === 1 ? isRestrictPopUpSave : false,
      isLoading: isSaving && activeStep === steps.length - 1,
    },
  ];

  return (
    <ActionsModalPopup
      open={open}
      onClose={onClose}
      title={isEdit ? "Edit Component" : "Add Component"}
      actions={actions}
    >
      {/* MUI Typography/Table default to Roboto while the app body uses
          Untitled Sans — keep the modal consistent with the app (86ajey3pj). */}
      <Box
        sx={{
          "& .MuiTypography-root, & .MuiTableCell-root, & .MuiButton-root": {
            fontFamily: "inherit",
          },
        }}
      >
        <CustomStepper
          steps={steps}
          activeStep={activeStep}
          onStepChange={(step) => setActiveStep(step)}
        />
      </Box>
    </ActionsModalPopup>
  );
};

export default EditComponentModal;
